import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { IAs2Partner } from '@core/models/as2-message.model';
import { ICertificateExpiry, IPartnerHealth } from '@core/models/integration.model';
import BreadCrumb from 'Common/BreadCrumb';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import as2IntegrationService from 'services/api/as2-integration.service';
import integrationsService from 'services/api/integrations.service';
import IntegrationPlatformSettingsPanel from './IntegrationPlatformSettingsPanel';
import EpcisCompanySettingsPanel from './EpcisCompanySettingsPanel';
import EpcisReadinessPanel from './EpcisReadinessPanel';
import IntegrationWizard from './IntegrationWizard';
import As2PartnerGroupModal from '../As2Integration/As2PartnerGroupModal';
import As2SectionNav from '../As2Integration/As2SectionNav';
import As2TestingGuide from '../As2Integration/As2TestingGuide';
import As2SetupGuide from '../As2Integration/As2SetupGuide';
import As2InboundGuide from '../As2Integration/As2InboundGuide';
import {
    formatConnectionTestResult,
    integrationErrorMessage,
} from './integration-user-messages';

function statusBadge(status?: string) {
    const s = (status || 'OK').toUpperCase();
    if (s === 'EXPIRED' || s === 'CRITICAL') return 'bg-red-100 text-red-800';
    if (s === 'WARNING') return 'bg-amber-100 text-amber-800';
    return 'bg-green-100 text-green-800';
}

function healthBadge(success?: number) {
    if (success == null) return 'bg-slate-100 text-slate-600';
    if (success >= 95) return 'bg-green-100 text-green-800';
    if (success >= 80) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
}

function formatMs(ms?: number) {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

function formatDate(iso?: string) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

const IntegrationsHub: FC = () => {
    const [partners, setPartners] = useState<IAs2Partner[]>([]);
    const [health, setHealth] = useState<IPartnerHealth[]>([]);
    const [certs, setCerts] = useState<ICertificateExpiry[]>([]);
    const [loading, setLoading] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [testingCode, setTestingCode] = useState<string | null>(null);
    const [editGroup, setEditGroup] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [pRes, hRes, cRes]: any[] = await Promise.all([
                as2IntegrationService.listPartners(),
                integrationsService.getHealth(),
                integrationsService.getCertificateExpiry(),
            ]);
            setPartners(pRes.data.as2Partners || []);
            setHealth(hRes.data.partnerHealth || []);
            setCerts(cRes.data.certificates || []);
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not load integration data.'), { autoClose: 5000 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const healthFor = (code?: string) => health.find((h) => h.partnerCode === code);

    const testConnection = async (code: string) => {
        setTestingCode(code);
        try {
            const r: any = await as2IntegrationService.testConnection(code);
            const formatted = formatConnectionTestResult(code, r.data);
            if (formatted.kind === 'success') {
                toast.success(formatted.message, { autoClose: 8000 });
                if (formatted.info) {
                    toast.info(formatted.info, { autoClose: 12000 });
                }
            } else {
                toast.error(formatted.message, { autoClose: 10000 });
            }
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Connection test failed.'), { autoClose: 5000 });
        } finally {
            setTestingCode(null);
        }
    };

    const toggleEnabled = async (partner: IAs2Partner) => {
        try {
            await as2IntegrationService.setEnabled(partner.code!, !partner.enabled);
            toast.success(`${partner.code} ${!partner.enabled ? 'enabled' : 'disabled'}`, { autoClose: 5000 });
            load();
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not update partner.'), { autoClose: 5000 });
        }
    };

    const grouped = partners.reduce<Record<string, IAs2Partner[]>>((acc, p) => {
        const g = p.partnerGroupCode || p.code || 'OTHER';
        if (!acc[g]) acc[g] = [];
        acc[g].push(p);
        return acc;
    }, {});

    return (
        <>
            <BreadCrumb pageTitle="AS2 Setup" />
            <section className="py-5">
                <div className="admin-motion-card flex flex-col gap-6 bg-white p-4">
                    <As2SectionNav />

                    <div
                        className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
                        role="status"
                    >
                        OpenAS2 <code className="font-mono text-xs">partnerships.xml</code> is auto-generated from
                        partners in the database when you save in the wizard or edit a partner. Do not edit{' '}
                        <code className="font-mono text-xs">openas2/data/generated/partnerships.xml</code> manually.
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h3 className="font-extrabold text-[#bd902e]">Integration Control Center</h3>
                            <p className="mt-1 max-w-2xl text-sm text-slate-500">
                                Onboard trading partners for <strong>production</strong> AS2/EPCIS. Use{' '}
                                <strong>Add partner</strong> to configure production URLs and certificates, complete
                                go-live checks, and enable live traffic. Manage existing partners under AS2 Partners.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Link
                                to={PRIVATEPATHS.SftpIntegrations}
                                className="inline-flex items-center rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                                SFTP (separate)
                            </Link>
                            <button
                                type="button"
                                onClick={() => setShowSettings((v) => !v)}
                                className="inline-flex items-center rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                                Platform settings
                            </button>
                            <ButtonBase innerText="Add partner" onClick={() => setWizardOpen(true)} />
                        </div>
                    </div>

                    {showSettings && <IntegrationPlatformSettingsPanel />}

                    <EpcisCompanySettingsPanel />

                    <EpcisReadinessPanel />

                    <div>
                        <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                            Certificate expiry
                        </h4>
                        <div className="overflow-x-auto rounded-md border border-slate-200">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                    <tr>
                                        <th className="px-3 py-2">Certificate</th>
                                        <th className="px-3 py-2">Partner</th>
                                        <th className="px-3 py-2">Expires in</th>
                                        <th className="px-3 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {certs.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="px-3 py-4 text-slate-500">
                                                {loading ? 'Loading…' : 'No certificate metadata yet — upload certs in the wizard.'}
                                            </td>
                                        </tr>
                                    )}
                                    {certs.map((c, i) => (
                                        <tr key={`${c.partnerCode}-${i}`} className="border-t border-slate-100">
                                            <td className="px-3 py-2 text-xs">
                                                {c.certSubject || c.partnerName || '—'}
                                            </td>
                                            <td className="px-3 py-2">{c.partnerCode}</td>
                                            <td className="px-3 py-2">
                                                {c.expiresInDays != null ? `${c.expiresInDays} days` : '—'}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span
                                                    className={`rounded px-2 py-0.5 text-xs font-medium ${statusBadge(c.status)}`}
                                                >
                                                    {c.status || 'OK'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div>
                        <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                            Partner health
                        </h4>
                        {loading ? (
                            <p className="text-sm text-slate-500">Loading partners…</p>
                        ) : (
                            <div className="space-y-4">
                                {Object.entries(grouped).map(([group, rows]) => (
                                    <div key={group} className="rounded-md border border-slate-200">
                                        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
                                            <span>{group}</span>
                                            <button
                                                type="button"
                                                onClick={() => setEditGroup(group)}
                                                className="rounded border border-slate-300 px-2 py-1 text-xs font-normal hover:bg-white"
                                            >
                                                Edit test/prod
                                            </button>
                                        </div>
                                        <div className="divide-y divide-slate-100">
                                            {rows.map((p) => {
                                                const h = healthFor(p.code);
                                                return (
                                                    <div
                                                        key={p.code}
                                                        className="flex flex-col gap-3 px-3 py-3 lg:flex-row lg:items-center lg:justify-between"
                                                    >
                                                        <div>
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className="font-medium text-slate-900">{p.name}</span>
                                                                <span className="font-mono text-xs text-slate-500">{p.code}</span>
                                                                <span
                                                                    className={`rounded px-2 py-0.5 text-xs ${
                                                                        p.environment === 'TEST'
                                                                            ? 'bg-blue-100 text-blue-800'
                                                                            : 'bg-purple-100 text-purple-800'
                                                                    }`}
                                                                >
                                                                    {p.environment || 'PRODUCTION'}
                                                                </span>
                                                                {!p.enabled && (
                                                                    <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600">
                                                                        Disabled
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="mt-1 break-all text-xs text-slate-500">{p.as2Url || '—'}</p>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
                                                            <div>
                                                                <span className="text-slate-500">Last MDN</span>
                                                                <p className="font-medium">{formatDate(h?.lastMdnAt)}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-slate-500">MDN success</span>
                                                                <p>
                                                                    <span
                                                                        className={`rounded px-1.5 py-0.5 font-medium ${healthBadge(h?.mdnSuccessPct)}`}
                                                                    >
                                                                        {h?.mdnSuccessPct != null
                                                                            ? `${h.mdnSuccessPct.toFixed(1)}%`
                                                                            : '—'}
                                                                    </span>
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <span className="text-slate-500">Failed MDN</span>
                                                                <p className="font-medium">
                                                                    {h?.failedMdnPct != null
                                                                        ? `${h.failedMdnPct.toFixed(1)}%`
                                                                        : '—'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <span className="text-slate-500">Avg response</span>
                                                                <p className="font-medium">{formatMs(h?.avgResponseTimeMs)}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                disabled={testingCode === p.code}
                                                                onClick={() => testConnection(p.code!)}
                                                                className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                                                            >
                                                                {testingCode === p.code ? 'Testing…' : 'Test URL'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleEnabled(p)}
                                                                className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                                                            >
                                                                {p.enabled ? 'Disable' : 'Enable'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <As2SetupGuide />
                    <As2InboundGuide />
                    <As2TestingGuide />
                </div>
            </section>

            <IntegrationWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onComplete={load} />
            {editGroup && (
                <As2PartnerGroupModal
                    open={Boolean(editGroup)}
                    groupCode={editGroup}
                    rows={grouped[editGroup] || []}
                    onClose={() => setEditGroup(null)}
                    onSaved={load}
                />
            )}
        </>
    );
};

export default IntegrationsHub;
